import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

/* ---------------------- Thunks ---------------------- */

// ✅ Fetch batches by product_record_id
export const fetchBatchesByProductId = createAsyncThunk(
  "productBatches/fetchBatchesByProductId",
  async (productRecordId, { rejectWithValue }) => {
    if (!productRecordId) {
      return rejectWithValue("Product record ID is required");
    }

    try {
      const response = await axios.get(
        `/api/v1/data-batches/product-batches/${productRecordId}`
      );
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch product batches"
      );
    }
  }
);

// ✅ Fetch pending batches
export const fetchPendingBatches = createAsyncThunk(
  "productBatches/fetchPendingBatches",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        "/api/v1/data-batches/get-pending-batches"
      );
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch pending batches"
      );
    }
  }
);

// ✅ Fetch batch data with this batch records from data store
// ✅ Fetch batch data with pagination
export const fetchBatchById = createAsyncThunk(
  "productBatches/fetchBatchById",
  async ({ batchId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/v1/data-batches/get-batch-data/${batchId}`,
        { params: { page, limit } } // 👈 send pagination params
      );
      return response.data; // ✅ keep both metadata + data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch batch data"
      );
    }
  }
);

// ✅ Upload batch CSV
export const uploadBatchCsv = createAsyncThunk(
  "productBatches/uploadBatchCsv",
  async ({ batch, file }, { rejectWithValue }) => {
    if (!file) return rejectWithValue("CSV file is required");

    const formData = new FormData();
    formData.append("csv", file);
    formData.append("batch_id", batch.id);
    formData.append("exhibitors_count", batch.exhibitors_count);
    formData.append(
      "event_name",
      `${batch.product?.event_name || ""} - ${batch.variant?.name || ""}`
    );
    formData.append("db_product_id", batch.product_id);
    formData.append(
      "woocommerce_product_id",
      batch.variant
        ? batch.variant.product_variant_id
        : batch.product?.woocommerce_product_id
    );

    try {
      const response = await axios.post(
        "/api/v1/data-batches/upload-batch-data",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to upload CSV"
      );
    }
  }
);

// New thunk for fetching all batches
export const fetchAllBatches = createAsyncThunk(
  "productBatches/fetchAllBatches",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/v1/data-batches/get-all-batches", {
        params: { page, limit },
      });
      return response.data; // includes metadata and data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch all batches"
      );
    }
  }
);

// ✅ Manually link a Google Sheet to a batch (paste URL or ID)
export const linkSheetToBatch = createAsyncThunk(
  "productBatches/linkSheetToBatch",
  async ({ batchId, sheetUrl }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/api/v1/data-batches/link-sheet/${batchId}`,
        { sheetUrl }
      );
      return { batchId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to link Google Sheet"
      );
    }
  }
);

// ✅ Fetch Google Sheet sync status for a batch
export const fetchSheetStatus = createAsyncThunk(
  "productBatches/fetchSheetStatus",
  async (batchId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/v1/data-batches/sheet-status/${batchId}`
      );
      return { batchId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch sheet status"
      );
    }
  }
);


/* ---------------------- Initial State ---------------------- */
const initialState = {
  data: [],        // all batches list
  batch: null,     // batch details with data + metadata
  loading: false,
  error: null,
  uploadStatus: null,

  allBatchesData: [],
  allBatchesMetadata: null,
  allBatchesLoading: false,
  allBatchesError: null,

  // Google Sheet state
  sheetGenerating: false,
  sheetError: null,
  sheetStatus: null,
};

/* ---------------------- Slice ---------------------- */
const productBatchesSlice = createSlice({
  name: "productBatches",
  initialState,
  reducers: {
    clearBatchesData: (state) => {
      state.data = [];
      state.batch = null;
      state.error = null;
      state.uploadStatus = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ fetchBatchesByProductId
      .addCase(fetchBatchesByProductId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBatchesByProductId.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchBatchesByProductId.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Failed to fetch product batches";
      })

      // ✅ fetchPendingBatches
      .addCase(fetchPendingBatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingBatches.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchPendingBatches.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Failed to fetch pending batches";
      })

      // ✅ fetchBatchById
      .addCase(fetchBatchById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBatchById.fulfilled, (state, action) => {
        state.loading = false;
        state.batch = action.payload; // { metadata, data }
      })
      .addCase(fetchBatchById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ uploadBatchCsv
      .addCase(uploadBatchCsv.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.uploadStatus = null;
      })
      .addCase(uploadBatchCsv.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadStatus = "success";
        // Update the batch in data with the updated batch from payload
        const updatedBatch = action.payload?.batch;
        if (updatedBatch) {
          const index = state.data.findIndex((b) => b.id === updatedBatch.id);
          if (index !== -1) {
            state.data[index] = { ...state.data[index], ...updatedBatch };
          }
        }
      })
      .addCase(uploadBatchCsv.rejected, (state, action) => {
        state.loading = false;
        state.uploadStatus = "error";
        state.error = action.payload;
      })

      // ✅ fetchAllBatches
      .addCase(fetchAllBatches.pending, (state) => {
        state.allBatchesLoading = true;
        state.allBatchesError = null;
      })
      .addCase(fetchAllBatches.fulfilled, (state, action) => {
        state.allBatchesLoading = false;
        state.allBatchesData = action.payload.data || [];
        state.allBatchesMetadata = action.payload.metadata || null;
      })
      .addCase(fetchAllBatches.rejected, (state, action) => {
        state.allBatchesLoading = false;
        state.allBatchesError = action.payload || "Failed to fetch all batches";
      })

      // ✅ linkSheetToBatch
      .addCase(linkSheetToBatch.pending, (state) => {
        state.sheetGenerating = true;
        state.sheetError = null;
      })
      .addCase(linkSheetToBatch.fulfilled, (state, action) => {
        state.sheetGenerating = false;
        const { batchId, google_sheet_url, google_sheet_id } = action.payload;
        const index = state.data.findIndex((b) => b.id === batchId);
        if (index !== -1) {
          state.data[index].google_sheet_url = google_sheet_url;
          state.data[index].google_sheet_id = google_sheet_id;
        }
      })
      .addCase(linkSheetToBatch.rejected, (state, action) => {
        state.sheetGenerating = false;
        state.sheetError = action.payload || "Failed to link Google Sheet";
      })

      // ✅ fetchSheetStatus
      .addCase(fetchSheetStatus.pending, (state) => {
        state.sheetError = null;
      })
      .addCase(fetchSheetStatus.fulfilled, (state, action) => {
        state.sheetStatus = action.payload;
      })
      .addCase(fetchSheetStatus.rejected, (state, action) => {
        state.sheetError = action.payload || "Failed to fetch sheet status";
      });
  },
});

/* ---------------------- Exports ---------------------- */
export const { clearBatchesData } = productBatchesSlice.actions;
export default productBatchesSlice.reducer;

